import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Check,
  ImagePlus,
  Loader2,
  Sparkles,
  Tag,
  Upload,
  User,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import PasteImageButton from "@/components/PasteImageButton";

/**
 * Upload Article page — the "I already wrote this piece elsewhere,
 * just get it into the Library" flow.
 *
 * The heavy lifting happens server-side: POST /articles/upload creates
 * the article + runs a lightweight LLM enrichment pass so the piece
 * lands with all the derivative fields (excerpt, meta description,
 * FB/IG/LinkedIn copy, photo suggestions, category suggestions) that
 * make the Library, WordPress publish button, and social share panels
 * work end-to-end.
 *
 * Image is optional here — after the upload returns, we can chain a
 * second call to POST /articles/{id}/upload-image if the editor picked
 * a file, reusing the existing endpoint. If they skip the image, the
 * article page shows the same "upload featured image" affordance
 * generated articles have.
 */
export default function UploadArticlePage() {
  const navigate = useNavigate();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });
  const wpAuthors = settings?.wordpress_authors || [];
  const wpCategories = settings?.wordpress_categories || [];

  const [headline, setHeadline] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [dek, setDek] = useState("");
  const [authorId, setAuthorId] = useState("");
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [catFilter, setCatFilter] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Opt-in LLM tweaks — both default off so the "body/headline is
  // FINAL" contract holds for editors who don't want assistance.
  const [addSubheads, setAddSubheads] = useState(false);
  const [generateSeoHeadline, setGenerateSeoHeadline] = useState(false);

  const filteredCats = useMemo(
    () =>
      wpCategories.filter((c) =>
        (c.name || "")
          .toLowerCase()
          .includes(catFilter.trim().toLowerCase()),
      ),
    [wpCategories, catFilter],
  );

  const toggleCategory = (id) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const recommend = useMutation({
    mutationFn: () =>
      api.recommendUploadCategories({
        headline: headline.trim(),
        body_html: bodyHtml.trim(),
      }),
    onSuccess: (r) => {
      const ids = r.category_ids || [];
      if (ids.length === 0) {
        toast.info(
          "The model didn't find a confident category match — try refining the headline or add more body content.",
        );
        return;
      }
      // Merge into existing selection so an editor can nudge the
      // model without losing their manual picks.
      setSelectedCats((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      const names = ids
        .map((id) => wpCategories.find((c) => c.id === id)?.name || `#${id}`)
        .join(", ");
      toast.success(`Recommended: ${names}`);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail ||
          "Could not fetch category recommendations",
      ),
  });

  const upload = useMutation({
    mutationFn: async () => {
      // 1. Create the article record + trigger the LLM enrichment
      //    pass. This is the slow call in the chain — 5-15 s while
      //    Claude drafts the social copy.
      const article = await api.uploadArticle({
        headline: headline.trim(),
        body_html: bodyHtml.trim(),
        dek: dek.trim(),
        wordpress_author_id: authorId ? parseInt(authorId, 10) : null,
        wordpress_categories: Array.from(selectedCats),
        add_subheads: addSubheads,
        generate_seo_headline: generateSeoHeadline,
      });
      // 2. Optional featured image — piggybacks on the same endpoint
      //    the generated-article flow uses so uploaded articles get
      //    the identical resize + convert-to-webp treatment. We
      //    proceed silently if this fails — the article is already
      //    saved, and the editor can re-upload from the article page.
      if (imageFile && article?.id) {
        try {
          await api.uploadArticleImage(article.id, imageFile);
        } catch (err) {
          toast.warning(
            "Article saved, but the featured image upload failed. Retry from the article page.",
          );
        }
      }
      return article;
    },
    onSuccess: (article) => {
      toast.success("Article added to Library");
      navigate(`/article/${article.id}`);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e?.message || "Upload failed",
      ),
  });

  const canRecommend =
    headline.trim().length >= 3 &&
    bodyHtml.trim().length >= 40 &&
    !recommend.isPending &&
    wpCategories.length > 0;
  const canSubmit =
    headline.trim().length >= 3 &&
    bodyHtml.trim().length >= 20 &&
    !upload.isPending;

  const onPickImage = (file) => {
    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const selectedCategoryPills = wpCategories.filter((c) =>
    selectedCats.has(c.id),
  );

  return (
    // Outer scroll wrapper — the App shell keeps its children inside
    // `flex-1 overflow-hidden`, so any page taller than the viewport
    // needs its own `flex-1 overflow-auto` here to scroll (mirrors the
    // pattern in LibraryPage / ResearchPage). Without this, the form
    // clips at the fold on shorter screens and the editor can't reach
    // the Send button.
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
      <div className="mb-8">
        <div className="overline mb-2">Newsroom · Upload</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">
          Upload Article
        </h1>
        <p className="mt-3 text-sm text-[var(--ink-muted)] max-w-2xl">
          Paste a pre-written article and Newsdesk fills in the WordPress
          excerpt, meta description, Facebook / Instagram / LinkedIn
          copy, and photo picks. The body you paste is <em>never</em>{" "}
          rewritten &mdash; only the surrounding metadata is generated.
        </p>
      </div>

      <div className="space-y-6 bg-white border border-[var(--border)] p-6">
        {/* Headline */}
        <div>
          <label
            htmlFor="upload-headline"
            className="overline mb-1.5 block"
          >
            Headline
          </label>
          <input
            id="upload-headline"
            data-testid="upload-headline"
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="The headline exactly as it should appear on the site"
            maxLength={300}
            className="w-full px-3 py-2 text-lg font-serif-display border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
          />
        </div>

        {/* Optional deck / subhead */}
        <div>
          <label htmlFor="upload-dek" className="overline mb-1.5 block">
            Subhead <span className="text-[var(--ink-muted)] normal-case tracking-normal">(optional)</span>
          </label>
          <input
            id="upload-dek"
            data-testid="upload-dek"
            type="text"
            value={dek}
            onChange={(e) => setDek(e.target.value)}
            placeholder="Optional one-liner that expands the headline"
            className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
          />
        </div>

        {/* Body — WYSIWYG editor (TipTap). Emits clean semantic HTML
            (<p>, <h2>, <h3>, <a>, <strong>, <em>, <ul>, <ol>,
            <blockquote>) so the downstream WordPress publisher keeps
            working unchanged. Pasting from Google Docs / Word retains
            structure but strips the MS-specific junk. */}
        <div>
          <label className="overline mb-1.5 block">
            Article body
            <span className="text-[var(--ink-muted)] normal-case tracking-normal ml-2">
              (rich text &mdash; paste from Docs/Word, use the toolbar,
              or type freely)
            </span>
          </label>
          <RichTextEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            testId="upload-body"
            placeholder="Paste the full article here. Formatting, headings, and links are preserved."
            minHeight={340}
          />
          <div className="text-[11px] text-[var(--ink-muted)] mt-1">
            {bodyHtml.replace(/<[^>]+>/g, "").length.toLocaleString()}{" "}
            characters ({bodyHtml.length.toLocaleString()} with markup)
          </div>
        </div>

        {/* Author */}
        <div>
          <label htmlFor="upload-author" className="overline flex items-center gap-1.5 mb-1.5">
            <User className="w-3 h-3" /> WordPress author
            {wpAuthors.length === 0 && (
              <span className="text-[10px] text-amber-700 normal-case tracking-normal">
                (no authors synced yet &mdash; Sync from Settings)
              </span>
            )}
          </label>
          <select
            id="upload-author"
            data-testid="upload-author"
            value={authorId}
            onChange={(e) => setAuthorId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
          >
            <option value="">&mdash; No author (uses API caller) &mdash;</option>
            {wpAuthors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        {/* Categories with Recommend button */}
        <div>
          <div className="flex items-center justify-between mb-1.5 gap-3">
            <label className="overline flex items-center gap-1.5">
              <Tag className="w-3 h-3" />
              Categories &middot; {selectedCategoryPills.length} selected
            </label>
            <button
              type="button"
              data-testid="upload-recommend-categories"
              onClick={() => recommend.mutate()}
              disabled={!canRecommend}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--accent)] text-[var(--accent-hover)] hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                wpCategories.length === 0
                  ? "Sync WordPress categories first"
                  : "Ask the writer LLM to suggest categories from the pasted content"
              }
            >
              {recommend.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3" />
              )}
              Recommend
            </button>
          </div>

          {selectedCategoryPills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedCategoryPills.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`upload-cat-pill-${c.id}`}
                  onClick={() => toggleCategory(c.id)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border border-[var(--accent)] bg-orange-50 text-[var(--accent-hover)] hover:bg-orange-100"
                  title="Click to remove"
                >
                  {c.name} <X className="w-2.5 h-2.5" />
                </button>
              ))}
            </div>
          )}

          {wpCategories.length > 0 && (
            <div>
              <input
                type="text"
                placeholder="Filter categories…"
                value={catFilter}
                onChange={(e) => setCatFilter(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)] mb-2"
              />
              <div className="max-h-40 overflow-y-auto border border-[var(--border)] divide-y divide-[var(--border)]">
                {filteredCats.map((c) => {
                  const on = selectedCats.has(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`upload-cat-option-${c.id}`}
                      onClick={() => toggleCategory(c.id)}
                      className={
                        "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-[var(--secondary)] " +
                        (on ? "text-[var(--accent-hover)]" : "")
                      }
                    >
                      <span
                        className={
                          "w-3.5 h-3.5 border shrink-0 grid place-items-center " +
                          (on
                            ? "bg-[var(--accent)] border-[var(--accent)]"
                            : "border-[var(--border)]")
                        }
                      >
                        {on && <Check className="w-2.5 h-2.5 text-white" />}
                      </span>
                      {c.name}
                    </button>
                  );
                })}
                {filteredCats.length === 0 && (
                  <div className="px-3 py-3 text-xs text-[var(--ink-muted)] italic">
                    No categories match &ldquo;{catFilter}&rdquo;
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Optional featured image */}
        <div>
          <label className="overline flex items-center gap-1.5 mb-1.5">
            <ImagePlus className="w-3 h-3" /> Featured image{" "}
            <span className="text-[var(--ink-muted)] normal-case tracking-normal">
              (optional)
            </span>
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-h-48 border border-[var(--border)]"
              />
              <button
                type="button"
                onClick={() => onPickImage(null)}
                className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center bg-white border border-[var(--border)] hover:border-red-400 hover:text-red-700"
                title="Remove image"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-[var(--border)] hover:border-[var(--ink)] cursor-pointer">
              <ImagePlus className="w-4 h-4" />
              Choose an image (JPG / PNG / WebP)
              <input
                type="file"
                accept="image/*"
                data-testid="upload-image-input"
                onChange={(e) => onPickImage(e.target.files?.[0] || null)}
                className="sr-only"
              />
            </label>
          )}
          {/* Mounted in both states: pasting with an image already
              chosen replaces it, exactly like picking a new file. */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <PasteImageButton
              onImage={(file) => onPickImage(file)}
              testId="upload-image-paste-btn"
              title="Paste an image from the clipboard as the featured image — or press Cmd/Ctrl+V anywhere on this page"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-dashed border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-60"
            />
          </div>
          <p className="text-[11px] text-[var(--ink-muted)] mt-1">
            Resized to 1200 px wide and converted to WebP on upload. You
            can also skip this and add an image later from the article
            page.
          </p>
        </div>

        {/* Opt-in LLM tweaks — surface before Submit so the editor
            reviews them alongside the other picks. Both are off by
            default; the copy under each label is intentionally
            explicit about what the model will modify. */}
        <div className="border-t border-[var(--border)] pt-4 space-y-3">
          <div className="overline">LLM assistance (optional)</div>
          <label
            data-testid="upload-toggle-subheads-label"
            className="flex items-start gap-2 text-sm cursor-pointer select-none"
          >
            <input
              type="checkbox"
              data-testid="upload-toggle-subheads"
              checked={addSubheads}
              onChange={(e) => setAddSubheads(e.target.checked)}
              className="mt-0.5 accent-[var(--slate-900)]"
            />
            <span>
              <span className="font-medium">Add subheads to the article</span>
              <span className="block text-[11px] text-[var(--ink-muted)] mt-0.5">
                Inserts 2&ndash;4 <code className="font-mono-display">&lt;h2&gt;</code>{" "}
                section headings between paragraphs. Paragraph text is
                preserved verbatim &mdash; only structure is added.
              </span>
            </span>
          </label>
          <label
            data-testid="upload-toggle-seo-headline-label"
            className="flex items-start gap-2 text-sm cursor-pointer select-none"
          >
            <input
              type="checkbox"
              data-testid="upload-toggle-seo-headline"
              checked={generateSeoHeadline}
              onChange={(e) => setGenerateSeoHeadline(e.target.checked)}
              className="mt-0.5 accent-[var(--slate-900)]"
            />
            <span>
              <span className="font-medium">
                Generate SEO-optimized headline
              </span>
              <span className="block text-[11px] text-[var(--ink-muted)] mt-0.5">
                Ignores the headline above and lets the LLM write one
                for search and clickability, following the property&rsquo;s
                house rules on length + tone.
              </span>
            </span>
          </label>
        </div>

        {/* Submit */}
        <div className="pt-4 border-t border-[var(--border)] flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="upload-submit"
            data-ai="true"
            onClick={() => upload.mutate()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:bg-[var(--ink)] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {upload.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading &amp; enriching&hellip;
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Send to Library
              </>
            )}
          </button>
          <span className="text-[11px] text-[var(--ink-muted)]">
            {upload.isPending
              ? "This takes about 10-20 seconds while the LLM writes the social copy."
              : "You'll land on the article page where every Library tool is available."}
          </span>
        </div>
      </div>
      </div>
    </div>
  );
}
